import type { PurchaseRequestItemOption, PurchaseRequestLineInput } from '../purchaseRequestTypes';

/**
 * Sample data for the purchase-request form.
 *
 * **It resolves against the LIVE catalog**, so a generated line always names an item the server
 * will accept — an invented id fails the "it must exist and be active" check, which reads as a
 * bug in the form rather than in the generator.
 *
 * **Nothing here is a person's name**, so the obviously-synthetic rule `samplePersonnel` runs
 * on does not bind: a request names items, a store and a reason, and none of it reaches
 * `rsys_pii`. The values are plausible vocabulary.
 *
 * **The URGENCY is not generated**, and that is deliberate — it decides whether the
 * justification field is required, so moving it would produce a different form on every click.
 * The justification is filled ANYWAY, whatever the urgency says, so one click leaves a
 * submittable form at every urgency the reader might choose.
 */
export function samplePurchaseRequest(
  items: PurchaseRequestItemOption[],
  facilities: { id: string; name: string; code: string }[],
): {
  facilityId: string | null;
  justification: string;
  notes: string;
  rows: PurchaseRequestLineInput[];
} {
  const rows: PurchaseRequestLineInput[] = pick(items, Math.min(2, items.length)).map((item) => ({
    inventoryItemId: item.id,
    requestedQuantity: 5 + Math.floor(Math.random() * 45),

    /*
      The last priced lot where there is one, and a plausible figure otherwise. NULL is left
      for a reader to produce by clearing the cell — a generator that sometimes filled it and
      sometimes did not would make the estimated total unpredictable between clicks.
    */
    estimatedUnitCost: item.lastUnitCost ?? round(50 + Math.random() * 450),
    justification: null,
  }));

  return {
    facilityId: facilities.length > 0 ? facilities[Math.floor(Math.random() * facilities.length)].id : null,
    justification: JUSTIFICATIONS[Math.floor(Math.random() * JUSTIFICATIONS.length)],
    notes: NOTES[Math.floor(Math.random() * NOTES.length)],
    rows,
  };
}

/** Distinct entries, so a generated request never puts one item on two lines. */
function pick<T>(from: T[], count: number): T[] {
  const pool = [...from];
  const taken: T[] = [];

  while (taken.length < count && pool.length > 0) {
    taken.push(...pool.splice(Math.floor(Math.random() * pool.length), 1));
  }

  return taken;
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

const JUSTIFICATIONS = [
  'Stock is below the reorder level and the next scheduled cycle is six weeks out.',
  'Consumption has run ahead of the plan for two quarters; the current holding covers about ten days.',
  'The existing units are past their useful life and are failing inspection.',
  'Required for the audit scheduled next month; nothing comparable is held.',
];

const NOTES = [
  'Coordinate with the receiving dock before delivery.',
  'Quotation attached; the supplier holds the price for thirty days.',
  'Partial delivery is acceptable if the full quantity is not available.',
  'Please confirm the unit of issue with the supplier before ordering.',
];
